import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { toFriendlyEdmMessage } from '../../utils/edmFriendlyMessages';
import { INVOICE_DOCS_PAGE_SIZE } from '../../utils/invoiceSchema';
import { paginateInvoiceList } from './invoiceMatchUtils';
import { iv } from './invoiceTheme';
import InvoicePeriodSelector, { ayEtiketi } from './InvoicePeriodSelector';
import OfficialInvoiceDialog from './official/OfficialInvoiceDialog';
import { fetchInboundInvoiceContent } from '../../utils/invoiceEdmApi';
import { buildOfficialModelFromInbound } from '../../utils/officialInvoiceModel';

const formatDay = (gun) => (gun ? String(gun).slice(0, 10).split('-').reverse().join('.') : '—');
const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function InvoiceInboundPanel({
  sube = '',
  rows = [],
  month,
  son6 = false,
  tumYil = false,
  onMonth,
  onSon6,
  onTumYil,
  loading,
  syncing,
  onSync
}) {
  // Satıra tıklayınca faturanın kendisi açılır (kabul/red öncesi incelemek için)
  const [invoiceView, setInvoiceView] = useState({ row: null, model: null, loading: false, error: '' });

  const openInvoice = async (row) => {
    if (row.ubl?.lines) {
      setInvoiceView({ row, model: buildOfficialModelFromInbound(row, row.ubl), loading: false, error: '' });
      return;
    }
    setInvoiceView({ row, model: null, loading: true, error: '' });
    try {
      const res = await fetchInboundInvoiceContent({ sube, docId: row.id });
      const model = buildOfficialModelFromInbound(row, res.ubl || {});
      setInvoiceView((prev) => (prev.row?.id === row.id ? { row, model, loading: false, error: '' } : prev));
    } catch (err) {
      const error = toFriendlyEdmMessage(err?.message) || 'Fatura içeriği alınamadı';
      setInvoiceView((prev) => (prev.row?.id === row.id ? { row, model: null, loading: false, error } : prev));
    }
  };
  const closeInvoice = () => setInvoiceView({ row: null, model: null, loading: false, error: '' });
  const [page, setPage] = useState(1);

  const list = useMemo(() => {
    // Siralama belge tarihine gore: arsiv toplu cekildigi icin syncedAt
    // butun kayitlarda ayni, siralama olcutu olamaz.
    return [...(rows || [])].sort((a, b) => {
      const fark = String(b.issueDate || '').localeCompare(String(a.issueDate || ''));
      if (fark) return fark;
      return String(b.invoiceId || '').localeCompare(String(a.invoiceId || ''));
    });
  }, [rows]);

  const pagination = useMemo(
    () => paginateInvoiceList(list, page, INVOICE_DOCS_PAGE_SIZE),
    [list, page]
  );

  // Donem degisince ilk sayfaya don.
  useEffect(() => {
    setPage(1);
  }, [month, son6, tumYil]);

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const toplamTutar = useMemo(
    () => (list || []).reduce((t, r) => t + (Number(r.payableAmount) || 0), 0),
    [list]
  );


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: iv.text }}>Gelen Belgeler</Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
            Tedarikçilerden gelen e-Faturalar · Kolaysoft arşivi
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon sx={{ fontSize: 18 }} />}
            onClick={onSync}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: iv.primary, '&:hover': { bgcolor: iv.primary } }}
          >
            Kolaysoft’tan Çek
          </Button>
        </Stack>
      </Box>

      {onMonth ? (
        <Box sx={{ flexShrink: 0 }}>
          <InvoicePeriodSelector
            month={month}
            son6={son6}
            tumYil={tumYil}
            onMonth={onMonth}
            onSon6={onSon6}
            onTumYil={onTumYil}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: iv.text }}>
              {tumYil ? `${String(month).slice(0, 4)} yılı` : son6 ? 'Son 6 ay' : `${ayEtiketi(month)} ayı`}
            </Typography>
            <Chip size="small" label={`${list.length.toLocaleString('tr-TR')} fatura`} sx={{ fontWeight: 800 }} />
            <Chip
              size="small"
              label={`${toplamTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}
              sx={{ fontWeight: 800, bgcolor: iv.primarySoft, color: iv.primary }}
            />
          </Stack>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : list.length === 0 ? (
        <Alert severity="info" sx={{ '& .MuiAlert-message': { fontWeight: 700, fontSize: 13 } }}>
          Bu dönemde gelen fatura yok. “Kolaysoft’tan Çek” ile senkronize edebilirsiniz.
        </Alert>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            borderColor: iv.border,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Table className="iv-inb-table" stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Fatura No', 'Tür', 'Tarih', 'Gönderen', 'TCKN / VKN', 'Tutar'].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i === 5 ? 'right' : 'left'}
                      sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, bgcolor: '#f8fafc' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagination.items.map((row) => {
                  const eArsiv = String(row.profileId || '').toUpperCase().includes('EARS');
                  return (
                    <TableRow
                      className="iv-inb-row"
                      key={row.id}
                      hover
                      onClick={() => openInvoice(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell className="iv-i-fatura" sx={{ fontSize: 12.5, fontWeight: 800 }}>
                        {row.invoiceId || row.uuid?.slice?.(0, 8) || '—'}
                      </TableCell>
                      <TableCell className="iv-i-tip">
                        <Chip
                          size="small"
                          label={eArsiv ? 'e-Arşiv' : 'e-Fatura'}
                          sx={
                            eArsiv
                              ? { fontWeight: 700, height: 20, bgcolor: '#f1f5f9', color: '#475569' }
                              : { fontWeight: 800, height: 20, bgcolor: '#dbeafe', color: '#1d4ed8' }
                          }
                        />
                      </TableCell>
                      <TableCell className="iv-i-tarih" sx={{ fontSize: 12.5, fontWeight: 700, color: iv.textMuted }}>
                        {formatDay(row.issueDate)}
                      </TableCell>
                      <TableCell className="iv-i-gonderen" sx={{ fontSize: 12.5, fontWeight: 700 }}>
                        {row.supplierTitle || '—'}
                      </TableCell>
                      <TableCell className="iv-i-vkn" sx={{ fontSize: 12.5, fontWeight: 600, color: iv.text }}>
                        {row.senderVkn || '—'}
                      </TableCell>
                      <TableCell
                        className="iv-i-tutar"
                        align="right"
                        sx={{ fontSize: 12.5, fontWeight: 800, color: iv.primary, whiteSpace: 'nowrap' }}
                      >
                        {formatMoney(row.payableAmount)} ₺
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={(_, next) => setPage(next + 1)}
            rowsPerPage={INVOICE_DOCS_PAGE_SIZE}
            rowsPerPageOptions={[INVOICE_DOCS_PAGE_SIZE]}
            labelRowsPerPage="Sayfa"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
            sx={{
              flexShrink: 0,
              borderTop: `1px solid ${iv.border}`,
              '.MuiTablePagination-toolbar': { minHeight: 40, px: 1 },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: 12,
                fontWeight: 700
              }
            }}
          />
        </Paper>
      )}
      <OfficialInvoiceDialog
        open={Boolean(invoiceView.row)}
        loading={invoiceView.loading}
        error={invoiceView.error}
        model={invoiceView.model}
        onClose={closeInvoice}
      />
    </Box>
  );
}

export default InvoiceInboundPanel;
